// LIBRARIES
import { createSlice } from "@reduxjs/toolkit";

// MISC
import { Tabs } from "models/GeneralTypes/generalTypes";
import {
  IArticle,
  ICategory,
  ILockerDetails,
  ISubcategory,
} from "models/interfaces";

export interface IUIInitialState {
  isLoading: Boolean;
  errorMessage: String;
  language: string;
  isSideMenuVisible: boolean;
  modalProps: {
    isVisible: Boolean;
    type: string;
    rentalUser?: boolean;
    user: unknown;
    report: unknown;
    door: {
      id: number;
      locker: ILockerDetails;
      identificationNumber: number;
      status: string;
      isActive: boolean;
      creationDate: string;
      lastUpdate: string;
      doorStatus: string;
    };
    rental: unknown;
    discount: unknown;
    category: ICategory;
    subcategory: ISubcategory;
    article: IArticle;
    promoCode: any;
  };
  snackbarProps: {
    isVisible: boolean;
    type: string;
    item: string;
  }
  childModalProps: {
    isVisible: boolean;
    type: string;
    content: string;
  };
  selectedTab: string;
  reportsTabs: boolean;
  auditTabs: boolean;
  invoicesTabs: boolean;
  lockerFace: string;
}

export const initialState: IUIInitialState = {
  isLoading: false,
  errorMessage: "",
  language: "en",
  isSideMenuVisible: false,
  modalProps: {
    isVisible: false,
    rentalUser: false,
    type: "",
    user: null,
    report: null,
    door: {
      id: 0,
      locker: {
        latitude: "",
        longitude: "",
        name: "",
        noOfAvailableSlots: "",
        noOfAvailableSupBoards: "",
        noOfSlots: "",
        noOfSupBoards: "",
        price: "",
        address: "",
        endHour: "",
        startHour: "",
        status: "",
      },
      identificationNumber: 0,
      status: "",
      isActive: true,
      creationDate: "",
      lastUpdate: "",
      doorStatus: "",
    },
    rental: null,
    discount: null,
    category: {
      id: 0,
      name: "",
    },
    subcategory: {
      id: 0,
      name: "",
      category: {
        id: 0,
        name: "",
      },
    },
    article: {
      id: 0,
      subcategory: {
        id: 0,
        name: "",
        category: {
          id: 0,
          name: "",
        },
      },
      title: "",
      content: "",
    },
    promoCode: {},
  },
  snackbarProps: {
    isVisible: false,
    type: "",
    item: "",
  },
  childModalProps: {
    isVisible: false,
    type: "",
    content: "",
  },
  selectedTab: Tabs.RENTALS,
  reportsTabs: false,
  auditTabs: false,
  invoicesTabs: false,
  lockerFace: "front",
};

export const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    toggleLoadingState: (state, action) => {
      state.isLoading = action.payload;
    },
    toggleModalState: (state, action) => {
      state.modalProps = action.payload;
    },
    toggleSnackbarState: (state, action) => {
      state.snackbarProps = action.payload;
    },
    toggleChildModalState: (state, action) => {
      state.childModalProps = action.payload;
    },
    toggleActiveTab: (state, action) => {
      state.selectedTab = action.payload;
    },
    toggleAuditTabs: (state) => {
      state.auditTabs = true;
      state.reportsTabs = false;
      state.invoicesTabs = false;
    },
    toggleReportsTabs: (state) => {
      state.auditTabs = false;
      state.reportsTabs = true;
      state.invoicesTabs = false;
    },
    toggleInvoicesTabs: (state) => {
      state.auditTabs = false;
      state.reportsTabs = false;
      state.invoicesTabs = true;
    },
    toggleLanguage: (state, action) => {
      state.language = action.payload;
    },
    toggleIsSideMenuVisible: (state, action) => {
      state.isSideMenuVisible = action.payload;
    },
    toggleLockerFace: (state, action) => {
      state.lockerFace = action.payload;
    }
  },
});

export const loadingState = (state) => state.ui.isLoading;

export const modalProps = (state) => state.ui.modalProps;

export const snackbarProps = (state) => state.ui.snackbarProps;

export const childModalProps = (state) => state.ui.childModalProps;

export const selectedTab = (state) => state.ui.selectedTab;

export const auditTabs = (state) => state.ui.auditTabs;

export const reportsTabs = (state) => state.ui.reportsTabs;

export const invoicesTabs = (state) => state.ui.invoicesTabs;

export const language = (state) => state.ui.language;

export const isSideMenuVisible = (state) => state.ui.isSideMenuVisible;

export const lockerFace = (state) => state.ui.lockerFace;

export const {
  toggleIsSideMenuVisible,
  toggleLoadingState,
  toggleModalState,
  toggleChildModalState,
  toggleActiveTab,
  toggleAuditTabs,
  toggleReportsTabs,
  toggleInvoicesTabs,
  toggleLanguage,
  toggleSnackbarState,
  toggleLockerFace,
} = uiSlice.actions;

export default uiSlice.reducer;
